import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, CheckCircle2, Settings, X } from 'lucide-react';
import { formatUiCurrencyDetailed } from '../utils/uiFormat';
import { customizedItemTotal, normalizeCustomizedItemQuantity } from '../utils/modifierQuantity';

const sortModifierOptions = (options = []) => [...options].sort((a, b) => {
  const aOrder = Number.isFinite(parseInt(a.sort_order, 10)) ? parseInt(a.sort_order, 10) : 0;
  const bOrder = Number.isFinite(parseInt(b.sort_order, 10)) ? parseInt(b.sort_order, 10) : 0;
  if (aOrder !== bOrder) return aOrder - bOrder;
  return String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' });
});

const sortPortionConfigs = (portionConfigs = []) => [...portionConfigs].sort((a, b) => {
  const aOrder = Number.isFinite(parseInt(a.sort_order, 10)) ? parseInt(a.sort_order, 10) : 0;
  const bOrder = Number.isFinite(parseInt(b.sort_order, 10)) ? parseInt(b.sort_order, 10) : 0;
  if (aOrder !== bOrder) return aOrder - bOrder;
  const optionA = Number.isFinite(parseInt(a.serving_size_option_detail?.sort_order, 10)) ? parseInt(a.serving_size_option_detail.sort_order, 10) : 0;
  const optionB = Number.isFinite(parseInt(b.serving_size_option_detail?.sort_order, 10)) ? parseInt(b.serving_size_option_detail.sort_order, 10) : 0;
  if (optionA !== optionB) return optionA - optionB;
  return String(a.serving_size_option_name || '').localeCompare(String(b.serving_size_option_name || ''), undefined, { sensitivity: 'base' });
});

const mapBaseComponents = (components = []) => (components || []).map((component) => {
  if (component?.items && component?.quantity_multiplier !== undefined) {
    return {
      menu_item_base_component_id: component.menu_item_base_component_id || null,
      recipe_component_id: component.recipe_component_id || component.recipe_component || null,
      name: component.name || component.recipe_component_name || component.recipe_component_detail?.name || '',
      quantity_multiplier: parseFloat(component.quantity_multiplier || 0),
      items: (component.items || []).map((item) => ({
        raw_material_id: item.raw_material_id || item.raw_material || null,
        raw_material_name: item.raw_material_name || '',
        unit_name: item.unit_name || '',
        quantity: parseFloat(item.quantity || 0),
      })),
    };
  }

  return {
    menu_item_base_component_id: component.id || component.menu_item_base_component_id || null,
    recipe_component_id: component.recipe_component_id || component.recipe_component || null,
    name: component.recipe_component_name || component.recipe_component_detail?.name || '',
    quantity_multiplier: parseFloat(component.quantity || 1),
    items: (component.recipe_component_detail?.items || []).map((item) => ({
      raw_material_id: item.raw_material,
      raw_material_name: item.raw_material_name,
      unit_name: item.unit_name,
      quantity: parseFloat(item.quantity || 0),
    })),
  };
});

const mapRecipeSnapshot = (ingredients = []) => (ingredients || []).map((ingredient) => ({
  raw_material_id: ingredient.raw_material_id || ingredient.raw_material || null,
  raw_material_name: ingredient.raw_material_name || ingredient.name || '',
  unit_name: ingredient.unit_name || '',
  quantity: parseFloat(ingredient.quantity || 0),
}));

const ModifierSelection = ({ isOpen, onClose, onConfirm, menuItem, initialData = null }) => {
  const portionConfigs = useMemo(() => sortPortionConfigs((menuItem?.portion_configs || []).filter((config) => {
    const currentMatch = initialData?.portion_id && String(config.id) === String(initialData.portion_id);
    return currentMatch || (config.is_available !== false && config.runtime_is_available !== false);
  })), [menuItem, initialData]);

  const [selectionMap, setSelectionMap] = useState({});
  const [baseComponents, setBaseComponents] = useState([]);
  const [expandedSections, setExpandedSections] = useState({ portion: true, base: false, modifiers: true });
  const [showDetailsFor, setShowDetailsFor] = useState({});
  const [selectedPortionId, setSelectedPortionId] = useState('');
  const [itemQuantity, setItemQuantity] = useState(1);

  const selectedPortionConfig = useMemo(
    () => portionConfigs.find((config) => String(config.id) === String(selectedPortionId)) || null,
    [portionConfigs, selectedPortionId]
  );

  // Modifiers may be tied to a specific portion (portion_config) or global (null).
  // When a portion is selected, show that portion's modifiers + any global ones.
  const groups = useMemo(() => {
    const allGroups = menuItem?.modifiers || [];
    const hasPortions = (menuItem?.portion_configs || []).length > 0;
    if (!hasPortions) return allGroups;
    const hasPerPortion = allGroups.some((g) => g.portion_config != null);
    if (!hasPerPortion) return allGroups; // legacy items: all modifiers are global
    return allGroups.filter((g) => (
      g.portion_config == null
      || String(g.portion_config) === String(selectedPortionId)
    ));
  }, [menuItem, selectedPortionId]);

  const recipeIngredientSnapshot = useMemo(() => {
    if (initialData?.portion_id && String(initialData.portion_id) === String(selectedPortionId) && (initialData.recipe_ingredients_snapshot || []).length > 0) {
      return mapRecipeSnapshot(initialData.recipe_ingredients_snapshot);
    }
    return mapRecipeSnapshot(selectedPortionConfig?.recipe_ingredients || menuItem?.recipe_ingredients || []);
  }, [initialData, menuItem, selectedPortionConfig, selectedPortionId]);

  useEffect(() => {
    if (!isOpen || !menuItem) {
      return;
    }

    const initialSelections = {};
    (initialData?.modifier_selections || []).forEach((selection) => {
      const key = `${selection.groupId}-${selection.modifier_option_id}`;
      initialSelections[key] = selection;
    });

    setSelectionMap(initialSelections);
    setShowDetailsFor({});
    setItemQuantity(normalizeCustomizedItemQuantity(initialData?.quantity));
  }, [isOpen, menuItem, initialData]);

  useEffect(() => {
    if (!isOpen || !menuItem) {
      return;
    }

    if (portionConfigs.length === 0) {
      setSelectedPortionId('');
      return;
    }

    // Honor a portion preselected from POS card shortcut buttons (B, R, L...)
    const initialPortionId = initialData?.portion_id
      || initialData?.menu_item_portion
      || menuItem?._preselectedPortionId
      || portionConfigs[0]?.id;
    setSelectedPortionId(String(initialPortionId || portionConfigs[0]?.id || ''));
  }, [isOpen, menuItem, initialData, portionConfigs]);

  useEffect(() => {
    if (!isOpen || !menuItem) {
      return;
    }

    const initialBaseComponents = initialData?.portion_id && String(initialData.portion_id) === String(selectedPortionId)
      ? (initialData.base_components || [])
      : (selectedPortionConfig?.base_components || menuItem.base_components || []);

    setBaseComponents(mapBaseComponents(initialBaseComponents));
  }, [isOpen, menuItem, initialData, selectedPortionConfig, selectedPortionId]);

  if (!isOpen || !menuItem) {
    return null;
  }

  const getSelectionsForGroup = (groupId) => Object.values(selectionMap).filter((selection) => selection.groupId === groupId);
  const getSelectedCount = (groupId) => getSelectionsForGroup(groupId).length;
  const getTotalQuantityForGroup = (groupId) => getSelectionsForGroup(groupId).reduce((sum, sel) => sum + (sel.quantity || 1), 0);

  const recalculateAutoSplit = (groupId, nextMap, basisQuantity) => {
    const groupSelections = Object.entries(nextMap).filter(([, selection]) => selection.groupId === groupId);
    if (groupSelections.length === 0 || !basisQuantity || basisQuantity <= 0) {
      return nextMap;
    }

    const splitQuantity = basisQuantity / groupSelections.length;
    const updated = { ...nextMap };
    groupSelections.forEach(([key, selection]) => {
      updated[key] = {
        ...selection,
        quantity_used: splitQuantity,
      };
    });
    return updated;
  };

  const toggleOption = (group, option) => {
    const groupId = group.modifier;
    const config = group.modifier_detail;
    const key = `${groupId}-${option.id}`;
    const existing = selectionMap[key];
    const allowMultiple = group.allow_multiple_quantities;
    const maxSelections = group.max_selections ?? 1;
    const currentTotalQuantity = getTotalQuantityForGroup(groupId);

    let next = { ...selectionMap };

    if (existing) {
      if (allowMultiple) {
        if (currentTotalQuantity >= maxSelections) {
          return;
        }
        const currentCount = existing.quantity || 1;
        next[key] = {
          ...existing,
          quantity: currentCount + 1,
        };
      } else {
        delete next[key];
      }
    } else {
      if (currentTotalQuantity >= maxSelections) {
        return;
      }

      if (!allowMultiple && maxSelections === 1) {
        Object.keys(next).forEach((existingKey) => {
          if (next[existingKey].groupId === groupId) {
            delete next[existingKey];
          }
        });
      }

      next[key] = {
        groupId,
        modifier_option_id: option.id,
        name: option.name,
        raw_material_id: option.raw_material,
        raw_material_name: option.raw_material_name,
        unit_name: option.unit_name,
        quantity_used: parseFloat(option.default_quantity || 0),
        price_adjustment: parseFloat(option.price_adjustment || 0),
        quantity: 1,
      };
    }

    if (config?.auto_split_quantity) {
      next = recalculateAutoSplit(groupId, next, parseFloat(config.auto_split_basis_quantity || 0));
    }

    setSelectionMap(next);
  };

  const decrementOption = (group, option) => {
    const groupId = group.modifier;
    const config = group.modifier_detail;
    const key = `${groupId}-${option.id}`;
    const existing = selectionMap[key];

    if (!existing) return;

    let next = { ...selectionMap };
    const currentCount = existing.quantity || 1;

    if (currentCount > 1) {
      next[key] = {
        ...existing,
        quantity: currentCount - 1,
      };
    } else {
      delete next[key];
    }

    if (config?.auto_split_quantity) {
      next = recalculateAutoSplit(groupId, next, parseFloat(config.auto_split_basis_quantity || 0));
    }

    setSelectionMap(next);
  };

  const updateSelectionField = (groupId, optionId, field, value) => {
    const key = `${groupId}-${optionId}`;
    if (!selectionMap[key]) {
      return;
    }
    setSelectionMap({
      ...selectionMap,
      [key]: {
        ...selectionMap[key],
        [field]: value,
      },
    });
  };

  const validate = () => {
    if (portionConfigs.length > 0 && !selectedPortionId) {
      return false;
    }
    return groups.every((group) => {
      const count = getSelectedCount(group.modifier);
      if (group.is_required && count < (group.min_selections ?? 0)) {
        return false;
      }
      if (count > (group.max_selections ?? 1)) {
        return false;
      }
      return true;
    });
  };

  const totalAdjustment = Object.values(selectionMap).reduce((sum, selection) => sum + (selection.price_adjustment || 0), 0);
  const basePrice = parseFloat(selectedPortionConfig?.price ?? menuItem.selling_price ?? 0);
  const totalUnitPrice = basePrice + totalAdjustment;

  const handleConfirm = () => {
    if (!validate()) {
      return;
    }
    onConfirm({
      modifier_selections: Object.values(selectionMap),
      base_components: baseComponents,
      price_adjustment: totalAdjustment,
      portion_id: selectedPortionConfig?.id || null,
      portion_name: selectedPortionConfig?.serving_size_option_name || '',
      serving_type_name: menuItem?.serving_type_detail?.name || '',
      base_price: basePrice,
      selected_unit_price: totalUnitPrice,
      quantity: itemQuantity,
      recipe_ingredients_snapshot: recipeIngredientSnapshot,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h3 className="text-lg font-bold text-gray-800">Customize {menuItem.name}</h3>
            <p className="text-sm text-gray-500">Confirm portion, price, and add modifiers if needed</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700"><X size={22} /></button>
        </div>

        <div className="p-4 space-y-4">
          {portionConfigs.length > 0 && (
            <div className="border rounded-xl">
              <button type="button" onClick={() => setExpandedSections({ ...expandedSections, portion: !expandedSections.portion })} className="w-full flex items-center justify-between p-4">
                <div>
                  <h4 className="font-semibold text-gray-800">{menuItem.serving_type_detail?.name || 'Portion'}</h4>
                  <p className="text-sm text-gray-500">Choose the portion before confirming the item</p>
                </div>
                {expandedSections.portion ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              </button>
              {expandedSections.portion && (
                <div className="px-4 pb-4 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {portionConfigs.map((portion) => {
                      const isSelected = String(portion.id) === String(selectedPortionId);
                      return (
                        <button
                          key={portion.id}
                          type="button"
                          onClick={() => setSelectedPortionId(String(portion.id))}
                          className={`rounded-xl border-2 p-4 text-left transition ${
                            isSelected
                              ? 'border-primary-600 bg-primary-50 shadow-sm'
                              : 'border-gray-200 hover:border-primary-300 hover:bg-primary-50/50'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-gray-800">{portion.serving_size_option_name}</span>
                                {isSelected ? <CheckCircle2 size={16} className="text-primary-600" /> : null}
                              </div>
                              {portion.serving_size_option_detail?.description ? (
                                <p className="text-xs text-gray-500 mt-1">{portion.serving_size_option_detail.description}</p>
                              ) : null}
                            </div>
                            <span className="text-sm font-bold text-primary-700">{formatUiCurrencyDetailed(portion.price || 0)}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  <div className="rounded-xl bg-primary-50 border border-primary-100 px-4 py-3 flex items-center justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-primary-700">Selected Portion Price</p>
                      <p className="font-semibold text-gray-800">
                        {selectedPortionConfig?.serving_size_option_name || 'Choose a portion'}
                      </p>
                    </div>
                    <p className="text-lg font-bold text-primary-700">{formatUiCurrencyDetailed(basePrice)}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="border rounded-xl">
            <button type="button" onClick={() => setExpandedSections({ ...expandedSections, modifiers: !expandedSections.modifiers })} className="w-full flex items-center justify-between p-4">
              <div>
                <h4 className="font-semibold text-gray-800">Modifiers</h4>
                <p className="text-sm text-gray-500">The selected portion stays on top. Modifier choices go here.</p>
              </div>
              {expandedSections.modifiers ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            </button>
            {expandedSections.modifiers && (
              <div className="px-4 pb-4 space-y-4">
                {groups.length === 0 ? (
                  <p className="text-sm text-gray-400">No modifiers assigned for this item.</p>
                ) : groups.map((group) => {
                  const config = group.modifier_detail;
                  const groupId = group.modifier;
                  const options = sortModifierOptions(config?.options || []).filter(option => option.is_available !== false);
                  return (
                    <div key={group.id} className="border rounded-lg p-3">
                      <div className="mb-3">
                        <h5 className="font-medium text-gray-800">{config?.display_name || group.modifier_name}</h5>
                        <p className="text-sm text-gray-500">
                          {group.is_required ? 'Required · ' : 'Optional · '}
                          Select {group.min_selections ?? 0}–{group.max_selections ?? 1}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {options.map((option) => {
                          const key = `${groupId}-${option.id}`;
                          const selected = selectionMap[key];
                          const selectionCount = selected?.quantity || 0;
                          const showDetails = showDetailsFor[key];
                          const allowMultiple = group.allow_multiple_quantities;
                          const maxSelections = group.max_selections ?? 1;
                          const currentTotalQuantity = getTotalQuantityForGroup(groupId);
                          const isMaxReached = currentTotalQuantity >= maxSelections;
                          const isDisabled = !selected && isMaxReached;

                          return (
                            <div key={option.id} className="relative">
                              {selected && allowMultiple ? (
                                <div className="flex items-center gap-1 bg-primary-50 border-2 border-primary-600 rounded-lg p-1">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      decrementOption(group, option);
                                    }}
                                    className="w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-lg font-bold flex items-center justify-center transition"
                                  >
                                    −
                                  </button>
                                  <div className="flex-1 text-center px-2">
                                    <div className="font-medium text-gray-800">{option.name}</div>
                                    <div className="text-lg font-bold text-primary-600">{selectionCount}</div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleOption(group, option);
                                    }}
                                    disabled={isMaxReached}
                                    className={`w-8 h-8 bg-green-500 hover:bg-green-600 text-white rounded-lg font-bold flex items-center justify-center transition ${
                                      isMaxReached ? 'opacity-50 cursor-not-allowed' : ''
                                    }`}
                                  >
                                    +
                                  </button>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => toggleOption(group, option)}
                                  disabled={isDisabled}
                                  className={`px-4 py-2 rounded-lg font-medium transition relative ${
                                    selected
                                      ? 'bg-primary-600 text-white border-2 border-primary-600'
                                      : 'bg-white text-gray-700 border-2 border-gray-300 hover:border-primary-400 hover:bg-primary-50'
                                  } ${isDisabled ? 'opacity-60 cursor-not-allowed' : 'hover:shadow-md'}`}
                                >
                                  {option.name}
                                </button>
                              )}
                              {selected && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowDetailsFor({ ...showDetailsFor, [key]: !showDetails });
                                  }}
                                  className="absolute -top-1 -right-1 bg-primary-500 text-white rounded-full p-1 hover:bg-primary-600 shadow-md"
                                  title="Adjust details"
                                >
                                  <Settings size={12} />
                                </button>
                              )}
                              {selected && showDetails && (
                                <div className="absolute top-full left-0 mt-2 p-3 bg-white rounded-lg border-2 border-primary-300 shadow-lg z-10 min-w-[200px]">
                                  <div className="mb-2">
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Quantity ({option.unit_name || ''})</label>
                                    <input
                                      type="number"
                                      step="0.001"
                                      value={selected.quantity_used}
                                      onChange={(e) => updateSelectionField(groupId, option.id, 'quantity_used', parseFloat(e.target.value || 0))}
                                      className="w-full px-3 py-2 border rounded-lg text-sm"
                                    />
                                  </div>
                                  {parseFloat(option.price_adjustment || 0) > 0 && (
                                    <div className="text-xs text-gray-600">+{formatUiCurrencyDetailed(option.price_adjustment || 0)} each</div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="border rounded-xl">
            <button type="button" onClick={() => setExpandedSections({ ...expandedSections, base: !expandedSections.base })} className="w-full flex items-center justify-between p-4">
              <div>
                <h4 className="font-semibold text-gray-800">Base Components</h4>
                <p className="text-sm text-gray-500">These change automatically with the selected portion.</p>
              </div>
              {expandedSections.base ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
            </button>
            {expandedSections.base && (
              <div className="px-4 pb-4 space-y-3">
                {baseComponents.length === 0 ? <p className="text-sm text-gray-400">No base components assigned.</p> : baseComponents.map((component) => (
                  <div key={`${component.recipe_component_id}-${component.name}`} className="border rounded-lg p-3">
                    <div className="font-medium text-gray-800">{component.name}</div>
                    <div className="mt-2 space-y-1 text-sm text-gray-500">
                      {component.items.map((item, index) => (
                        <div key={`${component.recipe_component_id}-${index}`}>
                          {item.raw_material_name} - {(item.quantity * component.quantity_multiplier).toFixed(3)} {item.unit_name || ''}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="border-t p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="space-y-1">
            <p className="text-sm text-gray-500">Base price</p>
            <p className="font-semibold text-gray-800">{formatUiCurrencyDetailed(basePrice)}</p>
            {totalAdjustment > 0 ? <p className="text-sm text-primary-600">Modifiers: +{formatUiCurrencyDetailed(totalAdjustment)}</p> : null}
            <div className="pt-2">
              <p className="text-xs uppercase tracking-wide text-gray-400 mb-1">Number of items</p>
              <div className="inline-flex items-center rounded-lg border border-gray-200 bg-white overflow-hidden">
                <button type="button" onClick={() => setItemQuantity(quantity => Math.max(1, quantity - 1))} disabled={itemQuantity <= 1} className="w-10 h-9 text-lg font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-30" aria-label="Decrease item count">−</button>
                <input type="number" min="1" step="1" value={itemQuantity} onChange={(event) => setItemQuantity(normalizeCustomizedItemQuantity(event.target.value))} className="w-14 h-9 border-x border-gray-200 text-center font-bold text-gray-800 focus:outline-none" aria-label="Number of customized items" />
                <button type="button" onClick={() => setItemQuantity(quantity => quantity + 1)} className="w-10 h-9 text-lg font-bold text-gray-700 hover:bg-gray-50" aria-label="Increase item count">+</button>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="text-right mr-1">
              <p className="text-xs uppercase tracking-wide text-gray-400">Item Total</p>
              <p className="text-lg font-bold text-primary-700">{formatUiCurrencyDetailed(customizedItemTotal(totalUnitPrice, itemQuantity))}</p>
              <p className="text-[11px] text-gray-400">{itemQuantity} × {formatUiCurrencyDetailed(totalUnitPrice)}</p>
            </div>
            <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg">Cancel</button>
            <button onClick={handleConfirm} className="px-4 py-2 bg-primary-600 text-white rounded-lg">
              {initialData ? 'Update Item' : 'Add to Cart'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModifierSelection;
